import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
// Inline SVG components to match the vendor form's design
const CloseIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

// New maximize icon
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

// New minimize icon
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const API_URL = '/api';

const TaxViewModal = ({ open, onClose, tax }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isMaximized, setIsMaximized] = useState(false);

  // Department feature: screen-config
  const [screenConfig, setScreenConfig] = useState(null);
  const SCREEN_NAME = 'TaxMaster';

  const truthy = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return Boolean(v);
  };

  const shouldShowDepartmentOnHeader = (cfg) => {
    if (!cfg) return false;
    const showHeader = truthy(cfg.ShowOnHeader) || truthy(cfg.showOnHeader);
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    return showHeader && showScreen;
  };

  const fetchScreenConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/screen-config/${encodeURIComponent(SCREEN_NAME)}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!res.ok) {
        let body = null;
        try { body = await res.json(); } catch (e) {}
        const msg = body?.message ? body.message : `Server returned ${res.status}`;
        throw new Error(msg);
      }

      const data = await res.json();
      const cfg = Array.isArray(data) ? (data[0] || null) : data;
      console.log('Screen info.....', cfg);
      setScreenConfig(cfg);
      return cfg;
    } catch (err) {
      console.error('fetchScreenConfig error:', err);
      setScreenConfig(null);
      return null;
    }
  };

  useEffect(() => {
    if (open && tax) {
      fetchScreenConfig();
    }
  }, [open, tax]);

  if (!open || !tax) return null;
  
  const getStatusColor = (status) => {
    const isActive = status === true || status === 'Active' || status === 1 || status === '1';
    return isActive ? 'bg-green-500 text-white' : 'bg-gray-500 text-white';
  };
  
  const getStatusText = (status) => {
    if (status === true || status === 1 || status === '1') return 'Active';
    if (status === false || status === 0 || status === '0') return 'Inactive';
    return status || 'Unknown';
  };
  
  const getDefaultText = (isDefault) => {
    if (isDefault === true || isDefault === 1 || isDefault === '1') return 'Yes';
    if (isDefault === false || isDefault === 0 || isDefault === '0') return 'No';
    return isDefault ? 'Yes' : 'No';
  };
  
  const getDefaultColor = (isDefault) => {
    const isDefaultValue = isDefault === true || isDefault === 1 || isDefault === '1';
    return isDefaultValue ? 'bg-blue-500 text-white' : 'bg-gray-500 text-white';
  };
  
  const sections = [
    {
      title: 'Tax Information',
      fields: [
        { label: 'Tax Code', value: tax.TaxCode },
        { label: 'Tax Type', value: tax.TaxType },
        { label: 'Tax Name', value: tax.TaxName },
        { label: 'Rate', value: `${tax.Rate}%` },
        { label: 'Country', value: tax.Country || '-' },

        // Department (Name ID Code inline) based on screen-config header visibility
        ...(shouldShowDepartmentOnHeader(screenConfig || tax?.screenConfig)
          ? [{
              label: 'Department',
              value: ` ${tax.DepartmentCode || '-'}`,
            }]
          : []),

        { 
          label: 'Default Tax', 
          value: tax.IsDefault, 
          type: 'isDefault',
          render: (value) => (
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm ${getDefaultColor(value)}`}>
              {getDefaultText(value)}
            </span>
          )
        },
        { 
          label: 'Status', 
          value: tax.Status, 
          type: 'status',
          render: (value) => (
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm ${getStatusColor(value)}`}>
              {getStatusText(value)}
            </span>
          )
        },
      ],
      name: 'Tax Info'
    },
    {
      title: 'Description',
      fields: [
        { label: 'Details', value: tax.Description || '-', isFullWidth: true },
      ],
      name: 'Description'
    },
    {
      title: 'System Information',
      fields: [
        { label: 'Created By', value: tax.CreatedBy || '-' },
        { 
          label: 'Created At', 
          value: tax.CreatedAt ? dayjs(tax.CreatedAt).format('YYYY-MM-DD HH:mm') : '-' 
        },
        { label: 'Updated By', value: tax.UpdatedBy || '-' },
        { 
          label: 'Updated At', 
          value: tax.UpdatedAt ? dayjs(tax.UpdatedAt).format('YYYY-MM-DD HH:mm') : '-' 
        },
      ],
      name: 'System Info'
    },
    {
      title: 'Tax Summary',
      fields: [
        { 
          label: 'Summary',
          value: (
            <div className="text-[#384959] leading-relaxed">
              <p>
                This tax is configured as <strong>{tax.TaxType}</strong> with a rate of <strong>{tax.Rate}%</strong>. 
                The tax is currently <strong>{getStatusText(tax.Status)}</strong> and is 
                {getDefaultText(tax.IsDefault) === 'Yes' ? ' set as the default tax' : ' not set as the default tax'}.
                {tax.Country && ` It applies to ${tax.Country}.`}
              </p>
            </div>
          ),
          isFullWidth: true
        }
      ],
      name: 'Summary'
    }
  ];
  
  // Navigation functions
  const handleNext = () => {
    if (currentStep < sections.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  // Toggle maximize/minimize
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  return (
  <div
    className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out
      ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
  >
    <style>
      {`
        .hide-scrollbar {
          -ms-overflow-style: none; /* Internet Explorer 10+ */
          scrollbar-width: none; /* Firefox */
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none; /* Safari and Chrome */
        }
        .custom-scrollbar::-webkit-scrollbar {
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}
    </style>
    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
    
    {/* This is the actual slide-in panel with fixed header and scrollable content */}
    <div 
      className={`bg-white shadow-2xl h-full flex flex-col
        transition-transform transform-gpu duration-300 ease-in-out
        ${open ? 'translate-x-0' : 'translate-x-full'}
        ${isMaximized ? 'w-full max-w-full' : 'w-full max-w-6xl'}`}
    >
      {/* Fixed Header */}
      <div className="flex-shrink-0 p-8 pb-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#6A89A7] rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#384959]">Tax Details</h2>
              <p className="text-[#64748b]">{tax.TaxCode}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button 
              className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1 rounded-full hover:bg-[#F1F5F9]" 
              onClick={toggleMaximize}
              title={isMaximized ? "Minimize" : "Maximize"}
            >
              {isMaximized ? <MinimizeIcon /> : <MaximizeIcon />}
            </button>
            <button 
              className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1 rounded-full hover:bg-[#F1F5F9]" 
              onClick={onClose}
              title="Close"
            >
              <CloseIcon />
            </button>
          </div>
        </div>
      </div>
      
      {/* Scrollable Content Area with all sections */}
      <div className="flex-grow overflow-y-auto scrollbar px-8">
        <div className="space-y-6 pb-6">
          {sections.map((section, index) => (
            <div key={index} className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <h3 className="text-lg font-semibold text-[#384959] mb-4">
                {section.title}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.fields.map((field, fieldIndex) => (
                  <div 
                    key={fieldIndex} 
                    className={`flex flex-col ${field.isFullWidth ? 'md:col-span-2' : ''}`}
                  >
                    <span className="text-sm font-medium text-[#64748b]">
                      {field.label}
                    </span>
                    <span className="text-base text-[#384959] mt-1 font-semibold break-words whitespace-normal uppercase">
                      {field.render ? field.render(field.value) : field.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Fixed Footer with Buttons */}
      <div className="flex-shrink-0 p-8 pt-0">
        <div className="flex justify-end gap-3">
          <button
            className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
);
};

export default TaxViewModal;